import { CheckCircle, Calendar, MessageCircle, Edit, Download, Share2 } from 'lucide-react';
import { BookingData } from './BookingFlow';
import { usePets } from '../../context/PetsContext';
import { toast } from 'sonner';

interface BookingConfirmationProps {
  bookingData: BookingData;
  onClose: () => void;
  bookingId?: string;
}

export function BookingConfirmation({ bookingData, onClose, bookingId }: BookingConfirmationProps) {
  const { pets } = usePets();
  const selectedPetsData = pets.filter(pet => bookingData.selectedPets.includes(pet.id));
  const appointmentId = bookingId || `APT-${Date.now().toString().slice(-8)}`;

  const handleAddToCalendar = () => {
    try {
      const date = bookingData.appointmentTime?.date;
      const time = bookingData.appointmentTime?.time || '12:00 PM';
      if (!date) { toast.error('No appointment date available'); return; }

      // Parse time (e.g., "02:00 PM")
      const [timePart, period] = time.split(' ');
      const [hStr, mStr] = timePart.split(':');
      let h = parseInt(hStr, 10);
      if (period?.toUpperCase() === 'PM' && h < 12) h += 12;
      if (period?.toUpperCase() === 'AM' && h === 12) h = 0;

      const startDate = new Date(date);
      startDate.setHours(h, parseInt(mStr || '0', 10), 0, 0);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // +1 hour

      const format = (d: Date) =>
        d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

      const petNames = selectedPetsData.map(p => p.name).join(', ');
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Petso//Booking//EN',
        'BEGIN:VEVENT',
        `DTSTART:${format(startDate)}`,
        `DTEND:${format(endDate)}`,
        `SUMMARY:${bookingData.category || 'Appointment'} - ${bookingData.professional?.name || 'Professional'}`,
        `DESCRIPTION:Pet appointment for ${petNames}\\nBooking ID: ${appointmentId}`,
        `LOCATION:${bookingData.professional?.name || ''}`,
        `UID:${appointmentId}@petso.io`,
        'END:VEVENT',
        'END:VCALENDAR',
      ].join('\r\n');

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `petso-appointment-${appointmentId}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Calendar file downloaded — open it to add to your calendar');
    } catch {
      toast.error('Failed to generate calendar file');
    }
  };

  const handleDownloadReceipt = () => {
    try {
      const date = bookingData.appointmentTime?.date;
      const time = bookingData.appointmentTime?.time || '';
      const petNames = selectedPetsData.map(p => `${p.name} (${p.breed})`).join(', ');
      const now = new Date().toLocaleString();

      const receiptHtml = `
<!DOCTYPE html>
<html>
<head><title>Petso Appointment Receipt</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 600px; margin: 40px auto; padding: 20px; color: #1a1a1a; }
  .header { text-align: center; padding: 24px; background: linear-gradient(135deg, #059669, #047857); color: white; border-radius: 12px; margin-bottom: 24px; }
  .header h1 { margin: 0 0 4px; font-size: 24px; }
  .header p { margin: 0; opacity: 0.9; font-size: 14px; }
  .section { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
  .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 14px; }
  .row:last-child { border-bottom: none; }
  .label { color: #6b7280; }
  .value { font-weight: 500; }
  .footer { text-align: center; font-size: 12px; color: #9ca3af; margin-top: 24px; }
  @media print { body { margin: 0; } }
</style>
</head>
<body>
  <div class="header">
    <h1>Petso</h1>
    <p>Appointment Receipt</p>
  </div>
  <div class="section">
    <div class="row"><span class="label">Appointment ID</span><span class="value">${appointmentId}</span></div>
    <div class="row"><span class="label">Date</span><span class="value">${date ? date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</span></div>
    <div class="row"><span class="label">Time</span><span class="value">${time}</span></div>
    <div class="row"><span class="label">Professional</span><span class="value">${bookingData.professional?.name || '—'}</span></div>
    <div class="row"><span class="label">Service</span><span class="value">${bookingData.category || '—'}</span></div>
    <div class="row"><span class="label">Pet(s)</span><span class="value">${petNames || '—'}</span></div>
  </div>
  <div class="footer">
    <p>Generated on ${now}</p>
    <p>Petso — Pet Health & Wellness Platform</p>
    <p>Free cancellation up to 24 hours before appointment</p>
  </div>
</body>
</html>`;

      const blob = new Blob([receiptHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `petso-receipt-${appointmentId}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch {
      toast.error('Failed to download receipt');
    }
  };

  const handleModifyAppointment = () => {
    toast.info('To modify your appointment, visit your Appointments page from the dashboard.', { duration: 4000 });
  };

  const handleChatWithProfessional = () => {
    toast.info('Chat with your professional will be available soon. Check your notifications for updates from your professional.', { duration: 4000 });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-8 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-3xl mb-2">Appointment Confirmed!</h2>
          <p className="text-lg opacity-90">Your booking has been successfully created</p>
        </div>

        <div className="p-8 space-y-6">
          {/* Appointment ID */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 mb-1">Appointment ID</div>
            <div className="text-2xl font-mono">{appointmentId}</div>
          </div>

          {/* Quick Details */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">When</div>
              <div className="mb-1">
                {bookingData.appointmentTime?.date.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
              </div>
              <div className="text-sm text-gray-600">{bookingData.appointmentTime?.time}</div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Where</div>
              <div className="mb-1">{bookingData.professional?.name}</div>
              <div className="text-sm text-gray-600">
                {bookingData.professional?.distance} away
              </div>
            </div>
          </div>

          {/* Pet(s) */}
          <div>
            <h4 className="mb-3">For</h4>
            <div className="space-y-2">
              {selectedPetsData.map(pet => (
                <div key={pet.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center text-2xl">
                    {pet.species === 'dog' ? '🐕' : '🐈'}
                  </div>
                  <div>
                    <div>{pet.name}</div>
                    <div className="text-sm text-gray-600">{pet.breed}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid md:grid-cols-2 gap-3">
            <button
              onClick={handleAddToCalendar}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Calendar className="w-5 h-5" />
              Add to Calendar
            </button>
            <button
              onClick={handleChatWithProfessional}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Chat with Professional
            </button>
            <button
              onClick={handleModifyAppointment}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-5 h-5" />
              Modify Appointment
            </button>
            <button
              onClick={handleDownloadReceipt}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Receipt
            </button>
          </div>

          {/* What's Next */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
            <h4 className="mb-4">What Happens Next?</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs">
                  1
                </div>
                <div>
                  <div className="mb-1">Confirmation Email Sent</div>
                  <div className="text-gray-600">Check your inbox for booking details and directions</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs">
                  2
                </div>
                <div>
                  <div className="mb-1">24-Hour Reminder</div>
                  <div className="text-gray-600">We'll send you a reminder before your appointment</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs">
                  3
                </div>
                <div>
                  <div className="mb-1">Professional Prepares</div>
                  <div className="text-gray-600">{bookingData.professional?.name} can now access the shared pet data</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs">
                  4
                </div>
                <div>
                  <div className="mb-1">Post-Appointment Updates</div>
                  <div className="text-gray-600">Receive notes, documents, and updates after your visit</div>
                </div>
              </div>
            </div>
          </div>

          {/* Cancellation Policy */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
            <strong>Cancellation Policy:</strong> Free cancellation up to 24 hours before appointment.
            You can reschedule or cancel anytime from your dashboard.
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Back to Dashboard
          </button>

          {/* Share */}
          <div className="text-center">
            <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mx-auto">
              <Share2 className="w-4 h-4" />
              Share Appointment Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
